// #4401: put a time limit on a LOCAL database operation that the boot waits on.
//
// The failure this exists for: the device's own store accepts an operation and then
// answers nothing at all. Not an error — nothing. A promise that never settles raises
// no rejection, so every try/catch around these calls is blind, and the boot simply
// stops. In production we measured reads outstanding for 30 seconds to over 4 minutes,
// and one customer sat through 73 minutes of it across several page reloads.
//
// PouchDB's IndexedDB adapter is what makes this silent rather than loud: it calls
// `indexedDB.open(name, version)` with success, error and upgrade handlers and NO
// `onblocked` handler and no time limit of its own, so if the browser never services
// that request, every later operation queues behind adapter setup for ever. The
// equivalent hole on the native SQLite adapter was closed in #3986 (L11) by
// `withSqliteOpenTimeout`, which this module deliberately mirrors — same latching, same
// injected timer, same "reject with a name the layer above can recognise" contract.
//
// What this does NOT do, because it cannot: cancel the underlying operation. Racing a
// timer abandons the wait; the operation itself stays pending against a store that is
// not answering. That is acceptable here, and only here, because every call site this
// is applied to has somewhere safe to go when the wait is abandoned — see each site.
//
// Keep this module free of Angular and browser-only imports: the unit test loads it
// under plain node and drives the clock by hand. Same pattern as local-op-tracker.ts.

import {
  clearRunningTimeTimeout,
  realTimerEnvironment,
  scheduleRunningTimeTimeout,
  TimerEnvironment,
} from './running-clock';

/**
 * How long a local database operation on the boot path may take before the boot stops
 * waiting for it.
 *
 * 15 seconds, matching `SQLITE_OPEN_TIMEOUT_MS` — the sibling bound on the other
 * adapter, for the same kind of failure. It is deliberately well under the 30 seconds
 * at which the splash watchdog reports a hang, so a store that has stopped answering is
 * handled before the user is told anything is wrong.
 *
 * Every operation this is applied to is small (a single `_local/` document read, or one
 * `bulkDocs` of the boot singletons), so 15 s is not a performance budget — it is far
 * beyond anything a working store takes, and its only job is to tell "working" apart
 * from "never coming back".
 *
 * It is 15 seconds of RUNNING time (#6093): time the phone kept the app frozen in the
 * background counts for at most a few seconds. See `runningTimeTimer` below.
 */
export const LOCAL_STORE_BOOT_OP_TIMEOUT_MS = 15_000;

/** The distinguishing error name. `interceptDatabaseError` matches on it — keep the two in sync. */
export const LOCAL_STORE_NOT_ANSWERING_NAME = 'LocalStoreNotAnswering';

/** True when this error is a local store that stopped answering, rather than one that failed. */
export function isLocalStoreNotAnsweringError(err: unknown): boolean {
  return !!err && typeof err === 'object'
    && (err as { name?: string }).name === LOCAL_STORE_NOT_ANSWERING_NAME;
}

/**
 * Build the silent-store error, carrying which database went silent (#6788).
 *
 * The database is the part of the operation name before the first colon, because every
 * caller names its operation with `localOpName(databaseId, …)`. The recovery needs it to
 * decide whether this store is the user's own brand-new one, which it may abandon.
 */
function localStoreNotAnsweringError(message: string, operationName: string): Error {
  const err = new Error(message);
  err.name = LOCAL_STORE_NOT_ANSWERING_NAME;
  const colon = operationName.indexOf(':');
  (err as { databaseId?: string }).databaseId = colon > 0 ? operationName.slice(0, colon) : undefined;
  return err;
}

/** Which database a silent-store error came from, or undefined when it does not say (#6788). */
export function localStoreNotAnsweringDatabaseId(err: unknown): string | undefined {
  if (!isLocalStoreNotAnsweringError(err)) return undefined;
  const databaseId = (err as { databaseId?: unknown }).databaseId;
  return typeof databaseId === 'string' ? databaseId : undefined;
}

export type ScheduleTimeout = (cb: () => void, ms: number) => unknown;
export type ClearTimeoutFn = (handle: unknown) => void;

/**
 * Race a local database operation against a time limit.
 *
 * Resolves or rejects with the operation's own outcome when it settles first. When the
 * time limit wins, rejects with an error whose `name` is
 * `LOCAL_STORE_NOT_ANSWERING_NAME`, so the layer above can tell this apart from an
 * operation that genuinely failed.
 *
 * The result is latched, so an answer arriving after the time limit is ignored rather
 * than resolving a promise the caller has already given up on, and the timer is always
 * cleared so it cannot leak.
 *
 * `operationName` travels into the rejection message and from there into telemetry, so
 * it must name the CALL and never carry a document body or a user value — the same rule
 * `local-op-tracker.ts` states for its own names.
 */
export function withLocalStoreTimeout<T>(
  operation: Promise<T>,
  operationName: string,
  timeoutMs: number,
  scheduleTimeout: ScheduleTimeout,
  clearTimeoutFn: ClearTimeoutFn,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const handle = scheduleTimeout(() => {
      if (settled) return;
      settled = true;
      reject(localStoreNotAnsweringError(
        `local store did not answer '${operationName}' within ${timeoutMs}ms`, operationName));
    }, timeoutMs);
    operation.then(
      value => {
        if (settled) return;
        settled = true;
        clearTimeoutFn(handle);
        resolve(value);
      },
      err => {
        if (settled) return;
        settled = true;
        clearTimeoutFn(handle);
        reject(err);
      },
    );
  });
}

/**
 * The bound every boot-path caller uses: the standard time limit, real timers (#4401).
 *
 * It exists because the same closure was being written out at each call site, and the
 * one thing a reader must be able to check at a glance is that they all wait the SAME
 * length of time — three hand-written copies is three chances for one of them to drift.
 * The full form stays exported for the tests, which drive the timer by hand.
 */
export function withBootLocalStoreTimeout<T>(
  operation: Promise<T>,
  operationName: string,
  env: TimerEnvironment = realTimerEnvironment(),
): Promise<T> {
  const timer = runningTimeTimer(env);
  return withLocalStoreTimeout(
    operation, operationName, LOCAL_STORE_BOOT_OP_TIMEOUT_MS, timer.schedule, timer.clear,
  );
}

/**
 * The timer functions every local-store limit is measured with: they count only time the page
 * was RUNNING (#6093).
 *
 * A phone that backgrounds the app freezes its JavaScript, and on waking every overdue timer
 * fires at once. A plain `setTimeout` limit therefore "expired" in the very first moment after
 * a freeze and gave up on a store that had had no running time to answer — in production it
 * rebuilt a working connection after a 4-minute freeze. See running-clock.ts for how a freeze
 * is told apart from a store that is silent while the page runs.
 */
export function runningTimeTimer(env: TimerEnvironment = realTimerEnvironment()):
  { schedule: ScheduleTimeout, clear: ClearTimeoutFn } {
  return {
    schedule: (cb, ms) => scheduleRunningTimeTimeout(cb, ms, env),
    clear: clearRunningTimeTimeout,
  };
}

/**
 * Race a local database operation against a time limit that FOLLOWS ITS PROGRESS.
 *
 * The difference from `withLocalStoreTimeout` above is what the limit measures. That
 * one limits how long the operation may take in total, which is right for a single
 * small read. This one limits how long the operation may go QUIET: every progress
 * signal clears the timer and starts it again. So an operation that keeps working is
 * never cancelled however long it takes, and one that stops working is still caught.
 *
 * #5448 is why it exists. A database copy on a real Android account takes about five
 * minutes, and it was being run under the total-time bound, so it was cancelled at
 * sixty seconds on every attempt and no real device could ever finish migrating.
 *
 * `subscribeToProgress` is handed a callback and returns a function that stops
 * listening. That function must be safe to call twice and safe to call after the
 * operation has ended — PouchDB removes every listener of its own when a replication
 * finishes, so by the time this cleans up there may be nothing left to remove.
 *
 * The rejection carries the SAME error name as the total-time bound, because the
 * layers above ask one question — "did the local store stop answering?" — and both
 * kinds of silence answer it the same way.
 *
 * Deliberately no throttle on the re-arm. The progress signal this was written for
 * fires once per document, so a large copy re-arms the timer tens of thousands of
 * times across several minutes. That is a few dozen timer operations a second, which
 * costs nothing, and a throttle would need a clock injected into what is otherwise a
 * function with no notion of the time of day.
 */
export function withLocalStoreIdleTimeout<T>(
  operation: PromiseLike<T>,
  operationName: string,
  idleTimeoutMs: number,
  subscribeToProgress: (onProgress: () => void) => () => void,
  scheduleTimeout: ScheduleTimeout,
  clearTimeoutFn: ClearTimeoutFn,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    let timerHandle: unknown;
    let unsubscribe: (() => void) | undefined;

    function stopWatching() {
      if (timerHandle !== undefined) clearTimeoutFn(timerHandle);
      timerHandle = undefined;
      try {
        unsubscribe?.();
      } catch (unsubscribeError) {
        // nothing left to unsubscribe from is the normal case, not a failure: the
        // operation tears its own listeners down when it ends. Reporting this would
        // replace the real outcome on its way out.
      }
    }

    function onIdle() {
      if (settled) return;
      settled = true;
      stopWatching();
      reject(localStoreNotAnsweringError(
        `local store made no progress on '${operationName}' for ${idleTimeoutMs}ms`, operationName));
    }

    function armTimer() {
      if (timerHandle !== undefined) clearTimeoutFn(timerHandle);
      timerHandle = scheduleTimeout(onIdle, idleTimeoutMs);
    }

    // listen BEFORE the first arm: a fake timer that fires straight away would
    // otherwise reach onIdle while the unsubscribe function is still unassigned
    unsubscribe = subscribeToProgress(() => {
      if (settled) return;
      armTimer();
    });
    armTimer();

    operation.then(
      value => {
        if (settled) return;
        settled = true;
        stopWatching();
        resolve(value);
      },
      err => {
        if (settled) return;
        settled = true;
        stopWatching();
        reject(err);
      },
    );
  });
}
